import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, Sparkles, Plus } from 'lucide-react';
import { CategoryTabs } from '../components/artwork/CategoryTabs';
import { ArtworkGrid } from '../components/artwork/ArtworkGrid';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getArtworks } from '../services/artworkService';
import { getRecentlyViewed } from '../services/recentlyViewedService';
import { Artwork, Category } from '../types';
import { motion } from 'framer-motion';

const Home: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categories: Category[] = ['All', 'Painting', 'Sculpture', 'Digital Art', 'Design', 'Handcraft'];

  useEffect(() => {
    loadArtworks();
  }, [activeCategory]);

  useEffect(() => {
    if (user) {
      loadRecentlyViewed();
    }
  }, [user]);

  const loadArtworks = async () => {
    setLoading(true);
    try {
      const data = await getArtworks(activeCategory);
      setArtworks(data);
    } catch (error) {
      console.error('Error loading artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentlyViewed = async () => {
    if (!user) return;
    try {
      const data = await getRecentlyViewed(user.id);
      setRecentlyViewed(data);
    } catch (error) {
      console.error('Error loading recently viewed:', error);
    }
  };

  const filteredArtworks = artworks.filter(artwork =>
    artwork.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    artwork.artistName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedArtworks = [...filteredArtworks].sort((a, b) => {
    if (a.isPromoted && !b.isPromoted) return -1;
    if (!a.isPromoted && b.isPromoted) return 1;
    return 0;
  });

  return (
    <div className="p-4 pb-24 bg-gradient-to-br from-purple-50 via-white to-blue-50 min-h-screen">
      {/* Animated Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-semibold text-gray-600">Hello, {user?.name || 'Artist'} 👋</h2>
            <h1 className="text-3xl font-bold gradient-text">Discover Amazing Art</h1>
          </div>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate('/post')}
            className="p-4 bg-gradient-to-r from-primary to-purple-600 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all"
          >
            <Plus size={24} />
          </motion.button>
        </div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="relative mb-6"
      >
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search for Art, Artists or Styles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-4 bg-white border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm hover:shadow-md transition-all"
        />
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-3 gap-3 mb-6"
      >
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white shadow-lg">
          <p className="text-2xl font-bold">{artworks.length}</p>
          <p className="text-xs opacity-90">Artworks</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 text-white shadow-lg">
          <p className="text-2xl font-bold">{artworks.filter(a => a.isPromoted).length}</p>
          <p className="text-xs opacity-90">Featured</p>
        </div>
        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl p-4 text-white shadow-lg">
          <p className="text-2xl font-bold">{categories.length - 1}</p>
          <p className="text-xs opacity-90">Categories</p>
        </div>
      </motion.div>

      {/* Categories */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-6"
      >
        <CategoryTabs
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
      </motion.div>

      {/* Trending Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="text-orange-500" size={24} />
            <h3 className="text-xl font-bold text-gray-800">Trending Now</h3>
          </div>
          <button 
            onClick={() => navigate('/explore')}
            className="text-primary text-sm font-medium hover:underline"
          >
            View all
          </button>
        </div>
        <ArtworkGrid artworks={sortedArtworks.slice(0, 6)} loading={loading} />
      </motion.div>

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-yellow-500" size={24} />
              <h3 className="text-xl font-bold text-gray-800">Recently Viewed</h3>
            </div>
            <button 
              onClick={() => setRecentlyViewed([])}
              className="text-primary text-sm font-medium hover:underline"
            >
              Clear
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {recentlyViewed.slice(0, 4).map((artwork, index) => (
              <motion.div
                key={artwork.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <div
                  onClick={() => navigate(`/artwork/${artwork.id}`)}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer card-hover"
                >
                  <img
                    src={artwork.imageUrl}
                    alt={artwork.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-3">
                    <h4 className="font-semibold text-gray-800 text-sm truncate">{artwork.title}</h4>
                    <p className="text-xs text-gray-500">{artwork.artistName}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Quick Actions Banner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.7 }}
        className="bg-gradient-to-r from-primary via-purple-600 to-pink-500 rounded-3xl p-6 text-white shadow-2xl relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10">
          <h3 className="text-2xl font-bold mb-2">Become a Featured Artist! ⭐</h3>
          <p className="text-sm opacity-90 mb-4">Promote your artworks and reach millions of art lovers</p>
          <button
            onClick={() => navigate('/promotion')}
            className="px-6 py-3 bg-white text-primary rounded-xl font-bold hover:shadow-lg transition-all"
          >
            View Plans
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Home;